import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Gift, Sparkles, Package, ShieldCheck, Truck, Award, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { COMBO_THEMES } from '../components/home/CombosSection';
import { useCart, DISCOUNT_CODES } from '../context/CartContext';
import { toast } from 'sonner';
import { SEO, pageSEO } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COMBO_CODE = 'COMBO10';
const COMBO_PERCENT = DISCOUNT_CODES[COMBO_CODE]?.percent || 10;

const formatPrice = (price) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(price);

export default function CombosPage() {
  const [categoryProducts, setCategoryProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const { addToCart, applyDiscountCode } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
    const allCats = [...new Set(COMBO_THEMES.flatMap((t) => t.categories))];
    Promise.all(
      allCats.map((c) =>
        fetch(`${API_URL}/api/products?category=${c}&limit=10`)
          .then((r) => r.json())
          .then((d) => [c, Array.isArray(d) ? d : []])
          .catch(() => [c, []])
      )
    )
      .then((entries) => setCategoryProducts(Object.fromEntries(entries)))
      .catch((e) => console.error('Error fetching combo products:', e))
      .finally(() => setLoading(false));
  }, []);

  const comboProductsMap = useMemo(() => {
    const map = {};
    COMBO_THEMES.forEach((theme) => {
      const picked = [];
      const seen = new Set();
      theme.categories.forEach((cat) => {
        const list = categoryProducts[cat] || [];
        const product = list.find((p) => !seen.has(p.id));
        if (product) {
          picked.push(product);
          seen.add(product.id);
        }
      });
      map[theme.id] = picked;
    });
    return map;
  }, [categoryProducts]);

  const handleAddCombo = (comboProducts) => {
    comboProducts.forEach((p) => p && addToCart(p));
    applyDiscountCode(COMBO_CODE);
    toast.success(`${comboProducts.length} items added! Extra ${COMBO_PERCENT}% off (${COMBO_CODE}) applied at checkout.`);
  };

  return (
    <div data-testid="combos-page">
      <SEO {...pageSEO.combos} url="https://astrovasturemedies.in/combos" />

      {/* Hero */}
      <section className="relative bg-gradient-to-r from-purple-900 via-indigo-900 to-blue-900 py-14 lg:py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-10 w-72 h-72 bg-purple-500 rounded-full blur-[100px]"></div>
          <div className="absolute bottom-10 right-10 w-72 h-72 bg-blue-500 rounded-full blur-[100px]"></div>
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-full mb-4 border border-white/20">
            <Gift className="w-5 h-5" />
            <span className="font-bold text-sm">SPECIAL COMBOS</span>
            <Sparkles className="w-5 h-5" />
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Curated{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-400">
              Combo Deals
            </span>
          </h1>
          <p className="text-white/70 max-w-2xl mx-auto text-base lg:text-lg">
            Hand-picked product combinations for maximum spiritual benefits. Each bundle is energized by expert
            astrologers — buy together and save more.
          </p>
        </div>
      </section>

      {/* Trust strip */}
      <div className="bg-amber-50 border-b border-amber-100 py-3">
        <div className="container mx-auto px-4 lg:px-8 flex flex-wrap justify-center gap-4 lg:gap-8">
          {[
            { icon: ShieldCheck, text: '100% Authentic' },
            { icon: Award, text: 'Lab Certified' },
            { icon: Sparkles, text: 'Energized by Astrologers' },
            { icon: Truck, text: 'Pan-India Delivery' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-2 text-sm text-amber-800">
              <item.icon className="w-4 h-4 text-amber-600" />
              <span className="font-medium">{item.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Combos List */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8 space-y-10">
          {loading
            ? [1, 2, 3, 4].map((i) => (
                <div key={i} className="h-56 bg-gray-100 rounded-2xl animate-pulse" />
              ))
            : COMBO_THEMES.map((theme, index) => {
                const products = comboProductsMap[theme.id] || [];
                const total = products.reduce((s, p) => s + (p?.price || 0), 0);
                const original = products.reduce((s, p) => s + (p?.originalPrice || p?.price || 0), 0);
                const bundleDiscount = Math.round((total * COMBO_PERCENT) / 100);
                const finalPrice = total - bundleDiscount;
                const totalSavings = original - finalPrice;

                return (
                  <motion.div
                    key={theme.id}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="rounded-2xl border border-amber-100 shadow-sm overflow-hidden"
                    data-testid={`combo-block-${theme.id}`}
                  >
                    <div className="grid lg:grid-cols-3 gap-0">
                      {/* Left: combo summary */}
                      <div className={`bg-gradient-to-br ${theme.color} p-6 lg:p-8 text-white flex flex-col`}>
                        <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center text-3xl mb-4 shadow-lg">
                          {theme.icon}
                        </div>
                        <h2 className="font-serif text-2xl font-bold mb-2">{theme.title}</h2>
                        <p className="text-white/80 text-sm mb-6">{theme.description}</p>
                        <div className="mt-auto">
                          <div className="flex items-baseline gap-2 mb-1">
                            <span className="text-3xl font-bold">{formatPrice(finalPrice)}</span>
                            <span className="text-lg text-white/60 line-through">{formatPrice(total)}</span>
                          </div>
                          <div className="inline-flex items-center gap-1.5 text-sm font-semibold bg-white/20 px-3 py-1 rounded-full mb-2">
                            <Sparkles className="w-3.5 h-3.5" />
                            Extra {COMBO_PERCENT}% OFF · code {COMBO_CODE}
                          </div>
                          {totalSavings > 0 && (
                            <p className="text-sm font-medium text-white/90 mb-3">
                              You Save {formatPrice(totalSavings)} in total
                            </p>
                          )}
                          <Button
                            onClick={() => handleAddCombo(products)}
                            disabled={products.length === 0}
                            className="w-full bg-white text-purple-900 hover:bg-white/90 font-semibold mt-1"
                            data-testid={`combo-add-${theme.id}`}
                          >
                            <Package className="w-4 h-4 mr-2" />
                            Add Combo to Cart
                          </Button>
                          <p className="text-[11px] text-white/70 mt-2 text-center">
                            {COMBO_PERCENT}% bundle discount auto-applied at checkout
                          </p>
                        </div>
                      </div>

                      {/* Right: included products */}
                      <div className="lg:col-span-2 p-6 lg:p-8">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-4">
                          This combo includes {products.length} items
                        </p>
                        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                          {products.map((product) => (
                            <Link
                              key={product.id}
                              to={`/product/${product.id}`}
                              className="group flex items-center gap-3 p-3 rounded-xl border border-amber-100 hover:border-amber-300 hover:shadow-md transition-all"
                              data-testid={`combo-product-${product.id}`}
                            >
                              <div className="w-16 h-16 rounded-lg overflow-hidden bg-amber-50 flex-shrink-0">
                                {product.image && (
                                  <img
                                    src={product.image}
                                    alt={product.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                  />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground line-clamp-2 group-hover:text-gold transition-colors">
                                  {product.name}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-sm font-bold text-gold">{formatPrice(product.price)}</span>
                                  {product.originalPrice > product.price && (
                                    <span className="text-xs text-muted-foreground line-through">
                                      {formatPrice(product.originalPrice)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 bg-amber-50/50">
        <div className="container mx-auto px-4 text-center">
          <h3 className="font-serif text-xl font-bold text-foreground mb-3">Want a custom combination?</h3>
          <p className="text-muted-foreground mb-5">Browse all products and create your own bundle.</p>
          <Link to="/products">
            <Button className="gold-gradient text-white px-8">
              Shop All Products
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
